import _ from "lodash";
import store from '@/store/index';
import { translation } from '@/plugins/i18n';
import { planTypes, zoomOneProMonthlyPlanCodes, zoomOneProYearlyPlanCodes } from '@/common/constants';
import { pageConfig } from '@/common/service/page-config';
import { dateTimeFormatWithUtcOffset } from '@/utils/dateformat';
import { isZoomPhone, PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import MessageBox from '@zoom/zoom-ui/src/components/message-box/message-box';

const PENDING_COUPON_DISABLED_SPECIAL_PLANS = [planTypes.ZOOM_PHONE, planTypes.TOLLFREE, planTypes.CMR];

const planListToObject = (planList = []) => {
  return _.reduce(planList, (res, item) => {
    const planCode = item?.plan_code;

    if (planCode) {
      res.planObject[planCode] = item;
      res.planCodeList.push(planCode);
    }

    return res;
  }, {
    planCodeList: [],
    planObject: {},
  });
};

const checkPendingCouponForMultiPlans = (currentPlans= [], editPlanType = "") => {
  const getters = store.getters;
  const boughtPlans = getters.boughtPlans;

  const oldPlans = _.filter(boughtPlans, (plan) => {
    if (isZoomPhone(editPlanType)) {
      return plan?.sub_type !== PBX_SUB_TYPES.PHONE_NUMBER;
    } else {
      return plan?.type === editPlanType;
    }
  });

  const isValid = validMultiPlansIsOnlyIncrease(oldPlans, currentPlans, editPlanType);

  if (!isValid) {
    return alertWithPendingCoupon();
  }

  return Promise.resolve();
}

const checkPendingCouponForSinglePlan = (currentPlans = [], editPlanType = "") => {
  const getters = store.getters;
  const oldPlan = getters.currentEditPlan;
  const nextPlan = _.get(currentPlans, [0]);

  const isValid = validPlanIsOnlyIncrease(oldPlan, nextPlan, editPlanType);

  if (!isValid) {
    return alertWithPendingCoupon();
  }

  return Promise.resolve();
}

// valid multiple plans are only increase|decrease case
const validMultiPlansIsOnlyIncrease = (oldPlans = [], currentPlans = [], editPlanType = "") => {
  const { planCodeList: oldPlanCodeList, planObject: oldPlansObject } = planListToObject(oldPlans);
  const { planCodeList: nextPlanCodeList } = planListToObject(currentPlans);

  // remove old plan case
  const isExistRemovePlan = _.some(oldPlanCodeList, (planCode) => {
    return _.includes(nextPlanCodeList, planCode) === false;
  });

  if (isExistRemovePlan) {
    return false;
  }

  return _.every(currentPlans, (nextPlan) => {
    const planCode = _.get(nextPlan, 'plan_code', '');

    if (_.has(oldPlansObject, planCode)) {
      return validPlanIsOnlyIncrease(oldPlansObject[planCode], nextPlan, editPlanType);
    } else {
      // new add plan case
      return true;
    }
  });
};

// valid current plan is only increase|decrease case
const validPlanIsOnlyIncrease = (oldPlan, nextPlan, editPlanType = "") => {
  const oldPlanCode = _.get(oldPlan, 'plan_code', '');
  const oldPlanBillingCycle = _.get(oldPlan, 'billing_cycle', '');
  const nextPlanCode = _.get(nextPlan, 'plan_code', '');
  const nextPlanBillingCycle = _.get(nextPlan, 'billing_cycle', '');

  let isNotIncrease = (oldPlanCode !== nextPlanCode)
    || oldPlanBillingCycle !== nextPlanBillingCycle;

  // PRO INCREASE case - if new Pro plan type exist, need to increase price
  if (zoomOneProMonthlyPlanCodes.includes(oldPlanCode) && zoomOneProMonthlyPlanCodes.includes(nextPlanCode)) {
    isNotIncrease = false;
  }
  if (zoomOneProYearlyPlanCodes.includes(oldPlanCode) && zoomOneProYearlyPlanCodes.includes(nextPlanCode)) {
    isNotIncrease = false;
  }

  // need compare newPlan and oldPlan license is desc
  // todo by arvin with audio plan
  if (editPlanType === planTypes.TOLLFREE) {
    const oldPlanTollFreeHosts = _.get(oldPlan, 'cartInfo.tollfree_apac_hosts', 0);
    const nextPlanTollFreeHosts = _.get(nextPlan, 'cartInfo.tollfree_apac_hosts', 0);

    isNotIncrease = isNotIncrease || nextPlanTollFreeHosts < oldPlanTollFreeHosts;
  } else {
    const oldPlanLicense = _.get(oldPlan, 'cartInfo.selected_number', 0);
    const nextPlanLicense = _.get(nextPlan, 'cartInfo.selected_number', 0);

    isNotIncrease = isNotIncrease || nextPlanLicense < oldPlanLicense;
  }

  return !isNotIncrease;
};

export const preCheckPendingCouponForUpgrade = async () => {
  if (store.getters.basicConfig?.hasPendingCoupon) {
    return alertWithPendingCoupon();
  }

  return Promise.resolve();
};

export const preCheckPendingCouponForEdit = async () => {
  const getters = store.getters;

  if (getters.basicConfig?.hasPendingCoupon) {
    const editPlanType = pageConfig.editConfig?.planType;
    const currentPlans = getters.getTypeGroupSelectedPlans[editPlanType];

    if (isZoomPhone(editPlanType) || PENDING_COUPON_DISABLED_SPECIAL_PLANS.includes(editPlanType)) {
      return checkPendingCouponForMultiPlans(currentPlans, editPlanType);
    } else {
      return checkPendingCouponForSinglePlan(currentPlans, editPlanType);
    }
  }

  return Promise.resolve();
}

export const alertWithPendingCoupon = async () => {
  const date = store.getters.basicConfig?.pendingCouponEffectiveDate;

  MessageBox.alert(
    translation(
      'billing.coupon.pending_coupon_desc',
      [dateTimeFormatWithUtcOffset(date)],
    ),
    translation('billing.buyItNow.cant_continue'),
    {
      dangerouslyUseHTMLString: true,
    },
  );

  return Promise.reject();
};


